import * as CONSTANT from '../constants';
import { ItemDetails } from '../models/item.model';
import { itemBuckets } from '../priceCalculation/buckets';
import {
  addAdditionalPrice,
  calculateAndUpdateTotalAdditionalPrice,
} from '../priceCalculation/helper';
import { bucketKeyForNonRequiredTray } from './helperMethods';

export function isSandwichTrayItem(item: any): boolean {
  return item?.is_sandwich_tray === 1;
}

export function getSingleItemTraysInPackage(packageItem: any) {
  return (
    packageItem?.package_categories
      ?.filter((tray: any) => tray.items?.length === 1)
      .map((tray: any) => tray.items[0]) ?? []
  );
}

export function resolveSubItemBucketSlotForTray(
  packageRoot: any,
  trayItem: any,
  order: any,
  manualSelectedItem?: ItemDetails,
  manualSelectedItemId?: number,
): number {
  if (!packageRoot?.is_package) {
    return CONSTANT.SINGLE_ITEM_SELECTED_SECTION;
  }
  if (
    manualSelectedItem &&
    manualSelectedItemId != null &&
    trayItem?.id != null &&
    trayItem.id === manualSelectedItem.id
  ) {
    return manualSelectedItemId;
  }
  const key = bucketKeyForNonRequiredTray(order, trayItem?.id);
  if (key >= 1) {
    return key;
  }
  const trays = getSingleItemTraysInPackage(packageRoot);
  const idx = trays.findIndex((t: any) => t.id === trayItem?.id);
  return idx >= 0 ? idx + 1 : CONSTANT.SINGLE_ITEM_SELECTED_SECTION;
}

export function resolveParentTrayItemForChips(
  packageRoot: any,
  fromItem: number,
  order: any,
  manualSelectedItem?: ItemDetails,
  manualSelectedItemId?: number,
): ItemDetails | null {
  if (!packageRoot) {
    return null;
  }
  if (!packageRoot.is_package) {
    return fromItem === CONSTANT.SINGLE_ITEM_SELECTED_SECTION ? packageRoot : null;
  }
  if (
    manualSelectedItem &&
    manualSelectedItemId != null &&
    fromItem === manualSelectedItemId
  ) {
    return manualSelectedItem;
  }
  const trays = getSingleItemTraysInPackage(packageRoot);
  const byIndex = trays[fromItem - 1];
  if (byIndex) {
    return byIndex;
  }
  const cat = packageRoot.package_categories?.find((c: any) => {
    if (c.items?.length !== 1) {
      return false;
    }
    return bucketKeyForNonRequiredTray(order, c.items[0].id) === fromItem;
  });
  return cat?.items?.[0] ?? null;
}

export function getSelectedSandwichChipsModifierId(
  fromItem: number,
  childItemGroupId: number | undefined,
): number | null {
  if (!childItemGroupId) {
    return null;
  }
  const mods =
    itemBuckets.specificItemBucketSelectedModifiers(
      CONSTANT.REQUIRED_MODIFIERS,
      fromItem,
    ) || [];
  const selected = mods.find(
    (m: any) =>
      m.related_modifier_group === childItemGroupId &&
      (m.quantity ?? 0) > 0,
  );
  return selected?.modifier_count ?? null;
}

/** null = group 195 not selected yet; 0 = No Chips; positive = chip cap */
export function resolveSandwichTrayChipsMax(
  parentTrayItem: any,
  fromItem: number,
  childItemID
): number | null {
  if (!isSandwichTrayItem(parentTrayItem)) {
    return null;
  }
  const modCount = getSelectedSandwichChipsModifierId(fromItem, childItemID);
  return modCount;
}

/**
 * Hide a sub-item group when a required modifier on this tray line targets it
 * (`related_modifier_group` === group id, selected quantity) and `modifier_count` is 0 (e.g. "No Chips").
 */
export function shouldHideTrayChipsGroup(
  parentTrayItem: any,
  fromItem: number,
  relatedSubItemGroupId: number,
): boolean {
  if (!isSandwichTrayItem(parentTrayItem) || !relatedSubItemGroupId) {
    return false;
  }
  return (
    resolveSandwichTrayChipsMax(
      parentTrayItem,
      fromItem,
      relatedSubItemGroupId,
    ) === 0
  );
}

/** Show all sub-item groups except those hidden by zero linked budget (e.g. No Chips). */
export function filterVisibleSubItemModifierGroups(
  groups: any[] | undefined,
  parentTrayItem: any,
  fromItem: number,
): any[] {
  if (!groups?.length) {
    return groups || [];
  }
  if (!isSandwichTrayItem(parentTrayItem)) {
    return groups;
  }
  return groups.filter(
    (g: any) =>
      resolveSandwichTrayChipsMax(parentTrayItem, fromItem, g.id) !== 0,
  );
}

export function trimSandwichTrayChipsSelections(params: {
  itemsAsModifier: any[];
  parentTrayItem: any;
  newChipsMax: number;
  order: any;
  dispatch: any;
  setItemsAsModifier: (v: any) => void;
  relatedGroup?: number;
}): void {
  const {
    itemsAsModifier,
    parentTrayItem,
    newChipsMax,
    order,
    dispatch,
    setItemsAsModifier,
    relatedGroup,
  } = params;
  if (!parentTrayItem) {
    return;
  }
  const groupDef = parentTrayItem.sub_items_required_modifier_groups?.find(
    (g: any) => g.id === relatedGroup,
  );
  if (!groupDef) {
    return;
  }
  const entryIdx = itemsAsModifier.findIndex(
    (row) =>
      row.id === relatedGroup
  );
  if (entryIdx < 0) {
    return;
  }
  calculateAndUpdateTotalAdditionalPrice(itemsAsModifier, '-', dispatch);
  const row = itemsAsModifier[entryIdx];
  let selectedItems = [...row.selectedItems];
  const inc = groupDef.item_counter_increment || 1;
  if (newChipsMax <= 0) {
    selectedItems = [];
  } else if (
    (groupDef.items_as_modifier?.length || 0) > 0 ||
    selectedItems.length > 0
  ) {
    const rowItemCount = groupDef.items_as_modifier?.length || 0;
    const divisor =
      rowItemCount > 0 ? rowItemCount : selectedItems.length || 1;
    const perItemShare = Math.floor(newChipsMax / divisor);
    const countVal = Math.floor(perItemShare / inc);
    const quantityEach = countVal * inc;
    const source =
      rowItemCount > 0 ? groupDef.items_as_modifier : selectedItems;
    selectedItems = source.map((item: any) => ({
      ...item,
      quantity: quantityEach,
      size_one_count: countVal,
      size_two_count: countVal,
      isSelected: true,
      ...(item.is_selected_with_count
        ? { isByDefault: true, actualQuantity: inc * countVal }
        : {}),
    }));
  }

  const repriced = addAdditionalPrice({
    items: selectedItems,
    currentItemsGroup: groupDef,
    order,
    relatedGroup
  });
  const next = [...itemsAsModifier];
  next[entryIdx] = { ...row, selectedItems: repriced };
  calculateAndUpdateTotalAdditionalPrice(next, '+', dispatch);
  setItemsAsModifier(next);
}
